import { request } from "@/services/network";
import { removeCjelina } from "@/utils/helpers";
import { localized } from "../../i18n/content.js";

// A short unit name ("Uvod") is ambiguous on its own, so it is shown with the
// name of what it belongs to.
export function unitTitle(name, contextName) {
	const short = removeCjelina(name);
	return short.length <= 12 ? `${short} - ${removeCjelina(contextName)}` : short;
}

// The same titles in English, falling back to Bosnian part by part.
const inEnglish = (item, field) => localized(item, field, "en");

// Only an English title that differs from the Bosnian one is worth indexing;
// a copy of the Bosnian title would count the same words twice. A null field
// is skipped by the index, and the UI falls back to `title`.
const differingTitle = (title, english) => (english === title ? null : english);

export function buildSearchIndex(data) {
	let course_categories = {};
	let course_thumbnails = {};

	for (let course of data?.courses || []) {
		if (course.slug) {
			course_categories[course.slug] = course.category;
			course_thumbnails[course.slug] = course.thumbnail;
		}
	}

	let kursevi = {
		id: 1,
		type: "kursevi",
		nameKey: "search:groups.courses",
		content: (data?.courses || []).map((course) => {
			return {
				id: course.slug,
				url: "/courses/" + course.slug,
				category: course.category,
				thumbnail: course.thumbnail,
				title: course.name,
				english_title: differingTitle(course.name, inEnglish(course, "name")),
				likes: course.like_count,
				modules: course.module_count,
				duration: course.duration,
				lecturers: course.lecturers || [],
			};
		}),
	};

	let videi = {
		id: 2,
		type: "video",
		nameKey: "search:groups.videos",
		content: (data?.videos || []).map((video) => {
			return {
				id: [video.course_slug, video.module_slug, video.slug].join(
					"/"
				),
				url: [
					"/courses",
					video.course_slug,
					video.module_slug,
					video.slug,
				].join("/"),
				courseName: video.course_name,
				moduleName: video.module_name,
				category: course_categories[video.course_slug],
				thumbnail: video.thumbnail,
				likes: video.like_count,
				views: video.view_count,
				duration: video.duration,
				title: unitTitle(video.name, video.course_name),
				english_title: differingTitle(
					unitTitle(video.name, video.course_name),
					unitTitle(
						inEnglish(video, "name"),
						inEnglish(video, "course_name")
					)
				),
			};
		}),
	};

	let kvizovi = {
		id: 3,
		type: "kvizovi",
		nameKey: "search:groups.quizzes",
		content: (data?.quizzes || []).map((quiz) => {
			return {
				id: [quiz.course_slug, quiz.module_slug, quiz.slug].join(
					"/"
				),
				url: [
					"/courses",
					quiz.course_slug,
					quiz.module_slug,
					quiz.slug,
				].join("/"),
				category: course_categories[quiz.course_slug],
				title: unitTitle(quiz.name, quiz.module_name),
				english_title: differingTitle(
					unitTitle(quiz.name, quiz.module_name),
					unitTitle(
						inEnglish(quiz, "name"),
						inEnglish(quiz, "module_name")
					)
				),
				questions: quiz.question_count,
				thumbnail: course_thumbnails[quiz.course_slug],
				courseName: quiz.course_name,
				moduleName: quiz.module_name,
			};
		}),
	};

	let paths = {
		id: 4,
		type: "path",
		nameKey: "search:groups.paths",
		content: (data?.paths || []).map((path) => {
			return {
				id: path.slug,
				url: `/paths/${path.slug}`,
				category: path.category || null,
				title: path.name,
				english_title: differingTitle(path.name, inEnglish(path, "name")),
				slug: path.slug,
				certificateId: path.certificate_id,
				thumbnail: path.thumbnail,
				ectsCredits: path.ects_credits,
				courseCount: path.course_count,
				priority: path.priority,
			};
		}),
	};

	return [kursevi, videi, kvizovi, paths];
}

export async function getSearchIndex() {
	return request({
		url: "/search/",
		method: "GET",
	}).then(buildSearchIndex);
}
